// F7: campos de formulário com rótulo, erro e ajuda; campos personalizados (N8) por entidade.
import { useQuery } from '@tanstack/react-query';
import type { InputHTMLAttributes, ReactNode, SelectHTMLAttributes, TextareaHTMLAttributes } from 'react';
import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import { api, ErroApi } from '../api';
import { textoDoErro, textoDoItem } from '../erros';
import {useSessao} from '../sessao';
import {ENTIDADES_EXTRAS_NATIVOS,lerMetadadosExtrasNativos,escopoCamposNativos,chaveCamposNativos} from './extrasNativos';

const base = 'w-full rounded-lg border bg-sup-2 px-3 text-sm text-tinta-1 outline-none placeholder:text-tinta-3 focus:border-acento disabled:opacity-60';

function Moldura({ id, rotulo, erro, ajuda, children, obrigatorio }: { id: string; rotulo: string; erro?: string | undefined; ajuda?: string | undefined; children: ReactNode; obrigatorio?: boolean | undefined }) {
  return (
    <div className="flex min-w-0 flex-col gap-1.5">
      <label htmlFor={id} className="text-sm text-tinta-2">
        {rotulo}
        {obrigatorio && <span className="text-acento"> *</span>}
      </label>
      {children}
      {erro ? <p id={`${id}-erro`} className="text-xs text-perigo">{erro}</p> : ajuda ? <p id={`${id}-ajuda`} className="text-xs text-tinta-3">{ajuda}</p> : null}
    </div>
  );
}

type Comum = { rotulo: string; erro?: string | undefined; ajuda?: string | undefined };
/** Liga a mensagem de erro (ou a ajuda) ao campo para o leitor de tela anunciar junto (aria-describedby). */
const descrito = (id: string, erro?: string, ajuda?: string) => (erro ? `${id}-erro` : ajuda ? `${id}-ajuda` : undefined);

export function CampoTexto({ rotulo, erro, ajuda, ...p }: Comum & InputHTMLAttributes<HTMLInputElement>) {
  const id = useId();
  return (
    <Moldura id={id} rotulo={rotulo} erro={erro} ajuda={ajuda} obrigatorio={p.required}>
      <input id={id} aria-invalid={!!erro} aria-describedby={descrito(id, erro, ajuda)} {...p} className={`${base} h-10 ${erro ? 'border-perigo' : 'border-borda'}`} />
    </Moldura>
  );
}

export function CampoArea({ rotulo, erro, ajuda, ...p }: Comum & TextareaHTMLAttributes<HTMLTextAreaElement>) {
  const id = useId();
  return (
    <Moldura id={id} rotulo={rotulo} erro={erro} ajuda={ajuda} obrigatorio={p.required}>
      <textarea id={id} aria-invalid={!!erro} aria-describedby={descrito(id, erro, ajuda)} rows={3} {...p} className={`${base} py-2 ${erro ? 'border-perigo' : 'border-borda'}`} />
    </Moldura>
  );
}

/**
 * ROB-02 (rodada 14): com `consulta` (a query que traz as opções), o campo mostra "Carregando" desabilitado enquanto
 * carrega e, se a consulta falha, a mensagem com "Tentar de novo" em vez de uma lista vazia em silêncio.
 */
export function CampoSelecao({
  rotulo,
  erro,
  ajuda,
  opcoes,
  vazio,
  consulta,
  ...p
}: Comum & SelectHTMLAttributes<HTMLSelectElement> & { opcoes: { valor: string; rotulo: string }[]; vazio?: string; consulta?: { isPending: boolean; isError: boolean; refetch: () => unknown } }) {
  const id = useId();
  const { t } = useTranslation();
  const carregando = !!consulta?.isPending;
  const falhou = !!consulta?.isError;
  return (
    <Moldura id={id} rotulo={rotulo} erro={erro} ajuda={ajuda} obrigatorio={p.required}>
      <select id={id} aria-invalid={!!erro || falhou} aria-busy={carregando} aria-describedby={descrito(id, erro, ajuda)} {...p} disabled={p.disabled || carregando || falhou} className={`${base} h-10 ${erro || falhou ? 'border-perigo' : 'border-borda'}`}>
        {carregando ? <option value="">{t('estado.carregando')}</option> : vazio !== undefined && <option value="">{vazio}</option>}
        {opcoes.map((o) => (
          <option key={o.valor} value={o.valor}>
            {o.rotulo}
          </option>
        ))}
      </select>
      {falhou && (
        <span className="flex items-center gap-2 text-xs text-perigo">
          {t('estado.erroTexto')}
          <button type="button" onClick={() => void consulta?.refetch()} className="text-acento-texto underline">
            {t('estado.tentar')}
          </button>
        </span>
      )}
    </Moldura>
  );
}

export function CampoMarcar({ rotulo, ...p }: { rotulo: string } & InputHTMLAttributes<HTMLInputElement>) {
  const id = useId();
  return (
    <label htmlFor={id} className="flex cursor-pointer items-center gap-2 text-sm text-tinta-2">
      <input id={id} type="checkbox" {...p} className="size-4" style={{ accentColor: 'var(--acento)' }} />
      {rotulo}
    </label>
  );
}

/**
 * Erros da API por campo. Códigos de domínio conhecidos viram mensagem no campo certo; o resto vai para o
 * topo do formulário.
 */
export function errosPorCampo(e: unknown, mapa: Record<string, string>, modulo?: string): { campos: Record<string, string>; geral: string | null } {
  if (!e) return { campos: {}, geral: null };
  if (!(e instanceof ErroApi)) return { campos: {}, geral: textoDoErro(e, modulo) };
  const todos = e.erros.length ? e.erros : [{ code: e.code, message: e.message, details: e.details }];
  const campos: Record<string, string> = {};
  const restantes: string[] = [];
  for (const x of todos) {
    const campoExtra = (x.details as { campo?: string } | undefined)?.campo;
    const campo = campoExtra && x.code.startsWith('campo_') ? `extras.${campoExtra}` : mapa[x.code];
    const texto = textoDoItem(x, modulo);
    if (campo && !campos[campo]) campos[campo] = texto;
    else restantes.push(texto);
  }
  return { campos, geral: restantes.length ? restantes.join(' ') : null };
}

interface CampoPersonalizado {
  id: string;
  chave: string;
  rotulo: string;
  tipo: 'texto' | 'numero' | 'data' | 'lista' | 'booleano';
  opcoes: string[];
  obrigatorio: boolean;
  legivel?: boolean;
  gravavel?: boolean;
}

/** N8: campos personalizados da entidade, dentro do formulário (valores em `extras`). */
export function CamposPersonalizados({ entidade, valores, aoMudar, aoAlterar, erros }: { entidade: string; valores: Record<string, unknown>; aoMudar: (v: Record<string, unknown>) => void; aoAlterar?: (chave:string,valor:unknown)=>void; erros?: Record<string, string> }) {
  const { t } = useTranslation();
  const nativa = (ENTIDADES_EXTRAS_NATIVOS as readonly string[]).includes(entidade);
  const escopo=escopoCamposNativos(useSessao());
  const q = useQuery({ queryKey: nativa?chaveCamposNativos(entidade,escopo):['nucleo', 'campos', entidade], enabled:!nativa||!!escopo,queryFn: async () => {const v=await api<CampoPersonalizado[]>(`/api/v1/nucleo/campos?entidade=${encodeURIComponent(entidade)}`);return nativa?lerMetadadosExtrasNativos(v,entidade):v;}, staleTime: 60_000 });
  if(nativa && (!escopo||q.isPending||q.isError))return <p role={q.isError?'alert':'status'}>{t(q.isError?'estado.erroTexto':'estado.carregando')}</p>;
  if (!q.data?.length) return null;
  const set = (k: string, v: unknown) => {const campo=q.data?.find(c=>c.chave===k);if(nativa&&(!campo||campo.legivel===false||campo.gravavel===false))return;if(aoAlterar)aoAlterar(k,v);else aoMudar({ ...valores, [k]: v });};
  return (
    <fieldset className="grid gap-4 sm:grid-cols-2">
      <legend className="kicker mb-3">{t('formulario.camposPersonalizados')}</legend>
      {q.data.filter(c=>!nativa||c.legivel!==false).map((c) => {
        const erro = erros?.[`extras.${c.chave}`];
        const disabled=nativa&&c.gravavel===false;
        const v = valores[c.chave];
        if (c.tipo === 'booleano') return <CampoMarcar key={c.id} rotulo={c.rotulo} checked={v === true} disabled={disabled} onChange={(e) => set(c.chave, e.target.checked)} />;
        if (c.tipo === 'lista')
          return <CampoSelecao key={c.id} rotulo={c.rotulo} required={c.obrigatorio} disabled={disabled} erro={erro} value={typeof v === 'string' ? v : ''} vazio="" opcoes={c.opcoes.map((o) => ({ valor: o, rotulo: o }))} onChange={(e) => set(c.chave, e.target.value || null)} />;
        return (
          <CampoTexto
            key={c.id}
            rotulo={c.rotulo}
            required={c.obrigatorio}
            disabled={disabled}
            erro={erro}
            type={c.tipo === 'numero' ? 'number' : c.tipo === 'data' ? 'date' : 'text'}
            value={v === undefined || v === null ? '' : String(v)}
            onChange={(e) => set(c.chave, c.tipo === 'numero' ? (e.target.value === '' ? null : Number(e.target.value)) : e.target.value || null)}
          />
        );
      })}
    </fieldset>
  );
}

/**
 * M12.4: empresa do grupo dona do registro. Lista só as empresas permitidas da sessão. Quem é restrito precisa
 * escolher uma (sem a opção "do grupo"); quem não é pode deixar o registro sem empresa.
 */
export function CampoEmpresa({ rotulo, valor, aoMudar, erro, restrito }: { rotulo?: string; valor: string | null; aoMudar: (v: string | null) => void; erro?: string; restrito: boolean }) {
  const { t } = useTranslation();
  const opcoes = useQuery({ queryKey: ['nucleo', 'empresas', 'opcoes'], queryFn: () => api<{ id: string; nome: string }[]>('/api/v1/nucleo/empresas/opcoes'), staleTime: 60_000 });
  return (
    <CampoSelecao
      rotulo={rotulo ?? t('empresa.rotulo')}
      required={restrito}
      erro={erro}
      value={valor ?? ''}
      onChange={(e) => aoMudar(e.target.value || null)}
      opcoes={(opcoes.data ?? []).map((o) => ({ valor: o.id, rotulo: o.nome }))}
      vazio={restrito ? t('empresa.escolha') : t('empresa.grupo')}
    />
  );
}
